import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, EmptyState, LoadingSkeleton } from "@/components/shared/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckSquare, Plus } from "lucide-react";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";

const PRIORITY = { Critical: "bg-red-500", High: "bg-amber-500", Normal: "bg-blue-400", Low: "bg-slate-300" };
const todayStr = new Date().toISOString().slice(0, 10);

export default function Tasks() {
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("today");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", type: "follow-up", priority: "Normal", due_date: todayStr });

  const load = async () => { setItems(null); const { data } = await api.get("/tasks?limit=200"); setItems(data.items); };
  useEffect(() => { load(); }, []);
  const create = async () => { if (!form.title) return toast.error("Title required"); await api.post("/tasks", form); toast.success("Task created"); setOpen(false); setForm({ title: "", type: "follow-up", priority: "Normal", due_date: todayStr }); load(); };
  const toggle = async (t) => { await api.put(`/tasks/${t.id}`, { status: t.status === "completed" ? "open" : "completed" }); load(); };

  const filtered = (items || []).filter((t) => {
    if (tab === "completed") return t.status === "completed";
    if (t.status === "completed") return false;
    if (tab === "overdue") return t.due_date && t.due_date < todayStr;
    if (tab === "today") return t.due_date === todayStr;
    if (tab === "upcoming") return t.due_date && t.due_date > todayStr;
    return true;
  });

  return (
    <div>
      <PageHeader title="My Tasks" subtitle="Stay on top of revenue-driving work" icon={CheckSquare}
        actions={<Button data-testid="new-task-button" className="aurelo-gradient text-white" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" />New Task</Button>} />
      <div className="flex gap-1.5 mb-4">
        {["today", "upcoming", "overdue", "completed"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize border ${tab === t ? "aurelo-gradient text-white border-transparent" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{t}</button>
        ))}
      </div>
      {items === null ? <LoadingSkeleton rows={5} /> : filtered.length === 0 ? (
        <EmptyState icon={CheckSquare} title="Nothing here" description="No tasks in this view. Create one to keep momentum." actionLabel="New Task" action={() => setOpen(true)} />
      ) : (
        <div className="rounded-2xl bg-white border border-slate-200 divide-y divide-slate-100">
          {filtered.map((t) => (
            <div key={t.id} data-testid="task-row" className="flex items-center gap-3 px-4 py-3">
              <Checkbox checked={t.status === "completed"} onCheckedChange={() => toggle(t)} />
              <span className={`h-2 w-2 rounded-full ${PRIORITY[t.priority] || PRIORITY.Normal}`} />
              <div className="flex-1 min-w-0"><div className={`text-sm font-medium ${t.status === "completed" ? "line-through text-slate-400" : "text-slate-900"}`}>{t.title}</div><div className="text-xs text-slate-400 capitalize">{t.type} · due {t.due_date}</div></div>
              {t.revenue_impact ? <span className="text-xs font-semibold text-emerald-600">{fmtMoney(t.revenue_impact)}</span> : null}
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Type</Label><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-2 text-sm">{["call", "email", "meeting", "follow-up", "proposal"].map((x) => <option key={x}>{x}</option>)}</select></div>
              <div><Label>Priority</Label><select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-2 text-sm">{["Critical", "High", "Normal", "Low"].map((x) => <option key={x}>{x}</option>)}</select></div>
              <div><Label>Due</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1" /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="aurelo-gradient text-white" onClick={create}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
